// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { LazyImageStyle } from "@conklin/MobiusOverrides/LazyImage";
import { slideshowStyles } from "@conklin/Overrides/Widgets/Basic/Slideshow";
import IconArrowRight from "@conklin/XDL/Icons/IconArrowRight";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { color, mergeWithCss, xScreen } from "@conklin/XDL/Utilities";
import { IconWrapper } from "@insite/mobius/Icon";
import { css } from "styled-components";

slideshowStyles.slideshowWrapper = {
    css: css`
        position: relative;
        border-radius: 4px;
        overflow: hidden;

        ${({ showSidesShadow }: any) =>
            showSidesShadow &&
            css`
                &::before,
                &::after {
                    content: "";
                    position: absolute;
                    top: 0;
                    height: 100%;
                    width: 90px;
                    z-index: 1;

                    ${xScreen("lte", "md")} {
                        display: none;
                    }
                }

                &::before {
                    left: 0;
                    background-image: linear-gradient(90deg, ${color("common.ui7")} 0%, ${color("common.ui7", 0)} 100%);
                }

                &::after {
                    right: 0;
                    background-image: linear-gradient(90deg, ${color("common.ui7", 0)} 0%, ${color("common.ui7")} 100%);
                }
            `}
    `,
};

slideshowStyles.slideContentWrapper = {
    ...mergeWithCss(slideshowStyles.slideContentWrapper, {
        css: css`
            ${xScreen("lte", "md")} {
                border-radius: 4px;
                flex-direction: column;
            }
        `,
    }),
};

slideshowStyles.pictureWrapper = {
    css: css`
        display: flex;
        max-height: 100%;
        max-width: 100%;

        ${xScreen("lte", "md")} {
            border-radius: 0 0 4px 4px;
            overflow: hidden;
        }

        ${LazyImageStyle} {
            width: auto;
            height: auto;

            max-height: 100%;
            .LazyImage-Placeholder {
                height: auto;
            }
            picture {
                display: flex;
                width: 100%;
                flex: 1;
                max-height: 100%;
                max-width: 100%;
            }
            img {
                flex: 1;
                width: 100%;
                height: auto;
                object-fit: contain;
            }
            &,
            img {
                aspect-ratio: 1334/320;

                ${xScreen("lte", "md")} {
                    aspect-ratio: 736/368;
                }
                ${xScreen("lte", 767)} {
                    aspect-ratio: 328/164;
                }
            }
        }
    `,
};

slideshowStyles.prevArrowWrapper = {
    css: css`
        position: absolute;
        z-index: 1;
        top: 50%;
        transform: translateY(-50%);
        left: 24px;
        z-index: 2;
    `,
};

slideshowStyles.prevArrowButton = slideshowStyles.nextArrowButton = {
    css: css`
        background: none;
        border: 0;
        padding: 0;
        &:not(:disabled) {
            &:hover,
            &:focus {
                background: none;
                border-color: transparent;
                outline: none;

                ${IconWrapper}, svg {
                    color: ${color("common.ui10")};
                }
            }
        }

        ${xScreen("lte", "md")} {
            margin-right: 12px;
            margin-left: 12px;
        }
    `,
};

slideshowStyles.nextArrowWrapper = {
    css: css`
        position: absolute;
        z-index: 1;
        top: 50%;
        transform: translateY(-50%);
        right: 24px;
        z-index: 2;
    `,
};

slideshowStyles.iconProps = {
    size: 24,
    css: css`
        &,
        svg {
            height: auto;

            ${xScreen("lte", "md")} {
                width: 12px;
            }
        }
    `,
};

slideshowStyles.slideOverlayWrapper = {
    css: css`
        border-radius: 4px;
        margin-left: 112px;
        width: 656px;
        z-index: 1;
        position: absolute;

        ${xScreen("lte", "md")} {
            background-color: ${color("common.ui7")};
            border-radius: 4px;
            margin: 4px 0 0 0;
            height: 243px;
            width: 100%;
            position: static;
        }

        ${xScreen("lte", "sm")} {
            height: 294px;
        }
    `,
};

slideshowStyles.headingText = {
    size: 36,
    lineHeight: "44px",
    weight: fontConfig.weight.black,
    forwardAs: "h1",
    css: css`
        margin-bottom: 16px;

        ${xScreen("lte", "md")} {
            font-size: 24px;
            line-height: 29px;
        }
    `,
};

slideshowStyles.subheadingText = {
    variant: "h2",
    css: css`
        margin-bottom: 0;

        ${xScreen("lte", "md")} {
            font-size: 18px;
            line-height: 22px;
        }
    `,
};

slideshowStyles.slideButton = {
    sizeVariant: "large",
    icon: {
        src: IconArrowRight,
        position: "right",
    },
    css: css`
        width: fit-content;
        margin-top: 20px;
        border: 1px solid ${color("common.ui1")};
    `,
};

slideshowStyles.dotsContainerWrapper = {
    css: css`
        display: flex;
        justify-content: center;
        align-items: center;
        bottom: 8px;
        position: absolute;
        background-color: ${color("common.ui7", 0.5)};
        padding: 8px;
        height: 21px;
        column-gap: 4px;
        left: 50%;
        transform: translateX(-50%);

        ${xScreen("lte", "md")} {
            bottom: 20px;
        }
    `,
};

slideshowStyles.dotButton = {
    css: css`
        height: 4px;
        width: 16px;
        min-height: 0;
        border: 1px solid ${color("common.ui1")};
        border-radius: 0;
        padding: 0;
        background-color: transparent;
        &:hover {
            border-color: ${color("common.ui10")};
            background-color: ${color("common.ui10")};
        }
        &:active {
            background-color: ${color("common.ui1")};
        }
        &:focus {
            outline: none;
        }
    `,
};

slideshowStyles.pauseButton = {
    css: css`
        background: none;
        border: none;
        min-height: 0;
        padding: 0;
        margin-left: 4px;

        &::after,
        &::before {
            content: "";
            height: 10px;
            width: 3px;
            background-color: ${color("common.ui1")};
            opacity: 0.5;
        }

        &::before {
            margin-right: 1px;
        }

        &.active {
            &:before {
                display: none;
            }
            &:after {
                height: 0;
                width: 0;
                border-top: 6px solid transparent;
                border-bottom: 6px solid transparent;
                border-left: 6px solid ${color("common.ui1")};
                background-color: transparent;
            }
        }

        &:not(:disabled):focus,
        &:not(:disabled):hover {
            background: none;
            outline: none;
        }

        &:not(.active):hover {
            &::after,
            &::before {
                background-color: ${color("common.ui10")};
                opacity: 1;
            }
        }
    `,
};
